import React, { useState } from 'react';
import { Check, X, Shield, DollarSign, TrendingUp, UserPlus, Crown } from 'lucide-react';
import { HiresRangeControl } from './HiresRangeControl';
import { FlipCard } from './FlipCard';
import { BuildFeeCard } from './BuildFeeCard';
import { buildInclusions, monthlyInclusions, salesSchoolInclusion, performanceInclusions, leaderBuildInclusions, leaderMonthlyInclusions, leaderPerformanceInclusions } from '../data/advantagePricing';
interface AdvantageStructureProps {
  buildFee: number;
  monthlyTotal: number;
  platformFee: number;
  campaignFee: number;
  recruiterFee: number;
  scopeMultiplier: number;
  hasRecruiter: boolean;
  onRecruiterChange: (value: boolean) => void;
  hasLeader: boolean;
  onLeaderChange: (value: boolean) => void;
  leaderBuildFee: number;
  leaderMonthlyFee: number;
  leaderActiveFee: number;
  splitBuildFee: boolean;
  onSplitBuildFeeChange: (value: boolean) => void;
  adSpend: number;
  onAdSpendChange: (value: number) => void;
  cpaLow: number;
  cpaHigh: number;
}
const usd = (value: number) => '$' + value.toLocaleString('en-US');
export function AdvantageStructure({
  buildFee,
  monthlyTotal,
  platformFee,
  campaignFee,
  recruiterFee,
  scopeMultiplier,
  hasRecruiter,
  onRecruiterChange,
  hasLeader,
  onLeaderChange,
  leaderBuildFee,
  leaderMonthlyFee,
  leaderActiveFee,
  splitBuildFee,
  onSplitBuildFeeChange,
  adSpend,
  onAdSpendChange,
  cpaLow,
  cpaHigh
}: AdvantageStructureProps) {
  const [openCards, setOpenCards] = useState({
    build: false,
    monthly: false,
    performance: false
  });
  const toggleCard = (card: 'build' | 'monthly' | 'performance') => setOpenCards(current => ({
    ...current,
    [card]: !current[card]
  }));

  // Spin every card a full turn when the campaign type changes, so the swap in
  // content reads as the same flip motion used to reveal pricing.
  const [spin, setSpin] = useState(0);
  const handleLeaderChange = (next: boolean) => {
    setSpin(current => current + 1);
    onLeaderChange(next);
  };
  const recruiterButton = <button type="button" onClick={() => onRecruiterChange(!hasRecruiter)} aria-pressed={hasRecruiter} className={`w-full flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-lg text-base font-semibold transition-colors duration-150 ease-out ${hasRecruiter ? 'bg-[#7FE0C3]/15 border border-[#7FE0C3]/40 text-[#7FE0C3] hover:bg-[#7FE0C3]/20' : 'bg-slate-800/50 border border-slate-700 text-slate-300 hover:border-[#7FE0C3]/40 hover:text-white'}`} data-id="element-48" data-mp="5gi45nzv826j">
      {hasRecruiter ? <>
          <X className="w-5 h-5" data-id="element-49" data-mp="3sj4tqqd4hn7c" />
          Remove recruiter
        </> : <>
          <UserPlus className="w-5 h-5" data-id="element-50" data-mp="3nsy4coasdyo1" />
          Add a recruiter
        </>}
    </button>;
  const leaderButton = <button type="button" onClick={() => handleLeaderChange(!hasLeader)} aria-pressed={hasLeader} className={`w-full flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-lg text-base font-semibold transition-colors duration-150 ease-out ${hasLeader ? 'bg-[#7FE0C3]/15 border border-[#7FE0C3]/40 text-[#7FE0C3] hover:bg-[#7FE0C3]/20' : 'bg-slate-800/50 border border-slate-700 text-slate-300 hover:border-[#7FE0C3]/40 hover:text-white'}`} data-id="element-51" data-mp="30226pajng98v">
      {hasLeader ? <>
          <X className="w-5 h-5" data-id="element-52" data-mp="15nn567j332li" />
          Standard campaign
        </> : <>
          <Crown className="w-5 h-5" data-id="element-53" data-mp="1tdracjbx7aem" />
          Find a leader
        </>}
    </button>;
  return <div data-id="element-54" data-mp="1yb0zdxvedk1x">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-55" data-mp="1yareupt4g53m">
        {/* Implementation */}
        <FlipCard flipped={openCards.build} onToggle={() => toggleCard('build')} spin={spin} label="Implementation" data-id="element-56" data-mp="1kbi26044jt4e">
          {{
          front: <div className="h-full bg-slate-900/40 border border-slate-800 rounded-xl p-6 flex flex-col" data-id="element-57" data-mp="8vbbxs5sjfm6">
                <div className="w-11 h-11 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700" data-id="element-58" data-mp="cw3lynmhloji">
                  <Shield className="w-5 h-5 text-[#7FE0C3]" data-id="element-59" data-mp="oe46u0w3bmt1" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1" data-id="element-60" data-mp="jfj7e3onx75n">
                  Implementation
                </h3>
                <p className="text-slate-400 text-xs mb-5" data-id="element-61" data-mp="eh7ufk32mgj7">
                  {hasLeader ? 'One-time leadership setup' : 'One-time setup'}
                </p>
                <ul className="space-y-2.5" data-id="element-62" data-mp="1c1ufs0iigcd0">
                  {(hasLeader ? leaderBuildInclusions : buildInclusions).map(point => <li key={point} className="flex items-start" data-id="element-63" data-mp="124pq3qpghyrg">
                        <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-64" data-mp="y4yrx1xlogcw" />
                        <span className="text-slate-300 text-base leading-snug" data-id="element-65" data-mp="3mw011xs4hbk4">
                          {point}
                        </span>
                      </li>)}
                </ul>
                <div className="mt-auto" data-id="element-66" data-mp="1ydnd3n5libyx">
                  {hasLeader ? <p className="pt-4 text-xs leading-snug text-slate-500" data-id="element-67" data-mp="22dphw56mnlif">
                      Must be built in conjunction with a standard campaign.
                    </p> : <p className={`pt-4 text-xs leading-snug ${hasRecruiter ? 'text-amber-400' : 'text-slate-500'}`} data-id="element-68" data-mp="2c4k0mzko02z4">
                      {hasRecruiter ? 'Included: a corporate recruiter aligned to your ICP, comp structure, and hiring standards before launch.' : 'Optional: a corporate recruiter aligned to your ICP and hiring standards before launch.'}
                    </p>}
                  <div className="pt-5" data-id="element-69" data-mp="vqafeebpyuci">{leaderButton}</div>
                </div>
              </div>,
          back: <div className="h-full bg-slate-900/60 border border-[#7FE0C3]/30 rounded-xl p-6 flex flex-col" data-id="element-70" data-mp="10pn1uxyohzek">
                <div className="text-xs text-[#7FE0C3] uppercase tracking-widest mb-3" data-id="element-71" data-mp="240auxvpouyf6">
                  Implementation
                </div>
                {hasLeader ? <div data-id="element-72" data-mp="2d1ihjgiueatf">
                    <div className="flex items-baseline" data-id="element-73" data-mp="2d1qwrf2fts7s">
                      <span className="text-4xl font-bold text-white" data-id="element-74" data-mp="28137oyxrrlg7">
                        {usd(leaderBuildFee)}
                      </span>
                      <span className="ml-2 text-sm text-slate-500" data-id="element-75" data-mp="2mwsjk498qi77">
                        one time
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-slate-500" data-id="element-76" data-mp="2vr700a5ibyrw">
                      Must be built in conjunction with a standard campaign.
                    </p>
                  </div> : <BuildFeeCard buildFee={buildFee} accent="mint" showLabel={false} split={splitBuildFee} onSplitChange={onSplitBuildFeeChange} data-id="element-77" data-mp="35q69fuy3jyfy" />}
                <ul className="mt-5 space-y-2.5 pt-4 border-t border-slate-800/50" data-id="element-78" data-mp="3pbg61ncb0f6k">
                  {(hasLeader ? leaderBuildInclusions : buildInclusions).map(point => <li key={point} className="flex items-start" data-id="element-79" data-mp="3fbxrjmf5xllw">
                        <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-80" data-mp="31lc6br28kzku" />
                        <span className="text-slate-400 text-base leading-snug" data-id="element-81" data-mp="31jhkmaj553fp">
                          {point}
                        </span>
                      </li>)}
                  {!hasLeader && hasRecruiter && <li className="flex items-start" data-id="element-82" data-mp="2wn0td7gna8ye">
                      <Check className="w-5 h-5 text-amber-400 mr-2.5 mt-0.5 shrink-0" data-id="element-83" data-mp="2imq60eejnchz" />
                      <span className="text-amber-400 text-base leading-snug" data-id="element-84" data-mp="2mo1w0l1bxzqp">
                        A corporate recruiter aligned to your ICP, comp
                        structure, and hiring standards before launch
                      </span>
                    </li>}
                </ul>
                <div className="mt-auto pt-5" data-id="element-85" data-mp="2t54u18ezafse">{leaderButton}</div>
              </div>
        }}
        </FlipCard>

        {/* Monthly Service */}
        <FlipCard flipped={openCards.monthly} onToggle={() => toggleCard('monthly')} spin={spin} label="Monthly Service" data-id="element-86" data-mp="2o61ta0616vq8">
          {{
          front: <div className="h-full bg-slate-900/40 border border-[#7FE0C3]/30 rounded-xl p-6 relative flex flex-col" data-id="element-87" data-mp="2fc5cl0vmybi7">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#7FE0C3] rounded-t-xl" data-id="element-88" data-mp="25g2b7x8lygwb" />
                <div className="w-11 h-11 rounded-full bg-[#7FE0C3]/10 flex items-center justify-center mb-4 border border-[#7FE0C3]/20" data-id="element-89" data-mp="20gpnla9wx5so">
                  <DollarSign className="w-5 h-5 text-[#7FE0C3]" data-id="element-90" data-mp="1wg6wjifug3rj" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1" data-id="element-91" data-mp="bxuybolv91sp">
                  Monthly Service
                </h3>
                <p className="text-[#7FE0C3] text-xs mb-5" data-id="element-92" data-mp="fxdn4vgt5x0t">
                  {hasLeader ? 'While the leadership campaign runs' : 'Recurring, month to month'}
                </p>
                <ul className="space-y-2" data-id="element-93" data-mp="pw30qc1o8vb9">
                  {(hasLeader ? leaderMonthlyInclusions : monthlyInclusions).map(point => <li key={point} className="flex items-start" data-id="element-94" data-mp="3tdut3rjpzh9z">
                      <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-95" data-mp="268pu00wxgtn" />
                      <span className="text-slate-300 text-base leading-snug" data-id="element-96" data-mp="6y9xt9r1sldv">
                        {point}
                      </span>
                    </li>)}
                  {!hasLeader && <li className="flex items-start" data-id="element-97" data-mp="3alseelqn2b2d">
                      <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-98" data-mp="169ium827qt8e" />
                      <span className="text-white text-base font-bold leading-snug" data-id="element-99" data-mp="1b8db33cpk5pc">
                        {salesSchoolInclusion}
                      </span>
                    </li>}
                  {!hasLeader && hasRecruiter && <li className="flex items-start" data-id="element-100" data-mp="1l618y25m1tbq">
                      <Check className="w-5 h-5 text-amber-400 mr-2.5 mt-0.5 shrink-0" data-id="element-101" data-mp="nj1326t4a3om" />
                      <span className="text-amber-400 text-base leading-snug" data-id="element-102" data-mp="shvprju7gce0">
                        Dedicated corporate recruiter owning your pipeline end
                        to end
                      </span>
                    </li>}
                </ul>
                {!hasLeader && <div className="mt-auto pt-5" data-id="element-103" data-mp="4sb24nn3wqye">{recruiterButton}</div>}
              </div>,
          back: <div className="h-full bg-slate-900/60 border border-[#7FE0C3]/50 rounded-xl p-6 relative flex flex-col" data-id="element-104" data-mp="9qkz93se61mn">
                <div className="absolute top-0 left-0 w-full h-1 bg-[#7FE0C3] rounded-t-xl" data-id="element-105" data-mp="2n0cptpgb8ok1" />
                <div className="text-xs text-[#7FE0C3] uppercase tracking-widest mb-2" data-id="element-106" data-mp="2j13skam2a8jq">
                  Monthly Service
                </div>
                <div className="flex items-baseline" data-id="element-107" data-mp="2e2slg7n5ic57">
                  <span className="text-4xl font-bold text-white" data-id="element-108" data-mp="290tkf4evvrbf">
                    {usd(hasLeader ? leaderMonthlyFee : monthlyTotal)}
                  </span>
                  <span className="ml-2 text-sm text-slate-500" data-id="element-109" data-mp="1zcblytu2tway">/ month</span>
                </div>

                <div className="mt-4 space-y-2 pt-3.5 border-t border-slate-800/50" data-id="element-110" data-mp="1va86lu4ae7ke">
                  <div className="flex justify-between text-sm" data-id="element-111" data-mp="1qbdfm4w61cp7">
                    <span className="text-slate-400" data-id="element-112" data-mp="1qcy71ln6ofiu">Partnership</span>
                    <span className="text-white font-medium" data-id="element-113" data-mp="1lb8ws87sc5vt">
                      {usd(platformFee)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-114" data-mp="1gfjv9zc7gewv">
                    <span className="text-slate-400" data-id="element-115" data-mp="16o0giszmdacz">
                      Pipeline management{' '}
                      <span className="text-slate-500" data-id="element-116" data-mp="1bq9b1ts54kh7">
                        ({hasLeader ? 2 : scopeMultiplier}x)
                      </span>
                    </span>
                    <span className="text-white font-medium" data-id="element-117" data-mp="11pqmwmtjmhth">
                      {usd(hasLeader ? leaderMonthlyFee - platformFee : campaignFee)}
                    </span>
                  </div>
                  {!hasLeader && hasRecruiter && <div className="flex justify-between text-sm" data-id="element-118" data-mp="1glyfobj0orkd">
                      <span className="text-slate-400" data-id="element-119" data-mp="1ko1qcxxv3s4l">
                        Corporate recruiter{' '}
                        <span className="text-slate-500" data-id="element-120" data-mp="28de1w4gnwca2">
                          ({scopeMultiplier}x)
                        </span>
                      </span>
                      <span className="text-white font-medium" data-id="element-121" data-mp="1pwuiwkgwnof">
                        {usd(recruiterFee)}
                      </span>
                    </div>}
                  <p className="pt-0.5 text-[11px] text-slate-500 leading-snug" data-id="element-122" data-mp="bg8bpkt5wtd9">
                    {hasLeader ? 'Priced at the $4,000 of ad spend a leadership campaign runs on.' : 'Pipeline management scales per $2,500 of ad spend under management.'}
                  </p>
                </div>

                <ul className="mt-4 space-y-1.5 pt-3.5 border-t border-slate-800/50" data-id="element-123" data-mp="39zf5t6fir5q6">
                  {(hasLeader ? leaderMonthlyInclusions : monthlyInclusions).map(point => <li key={point} className="flex items-start" data-id="element-124" data-mp="3jynv3lzfvw67">
                      <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-125" data-mp="143m02uisoglr" />
                      <span className="text-slate-400 text-base leading-snug" data-id="element-126" data-mp="z5aq8jsi263j">
                        {point}
                      </span>
                    </li>)}
                  {!hasLeader && <li className="flex items-start" data-id="element-127" data-mp="39tpkv9fknr3g">
                      <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-128" data-mp="3duro01cm0nap" />
                      <span className="text-white text-base font-bold leading-snug" data-id="element-129" data-mp="3iunjfc78r6en">
                        {salesSchoolInclusion}
                      </span>
                    </li>}
                  {!hasLeader && hasRecruiter && <li className="flex items-start" data-id="element-130" data-mp="3sq7clquf7qzp">
                      <Check className="w-5 h-5 text-amber-400 mr-2.5 mt-0.5 shrink-0" data-id="element-131" data-mp="1de4zp3a7la9" />
                      <span className="text-amber-400 text-base leading-snug" data-id="element-132" data-mp="5fr9yba7ff5z">
                        Dedicated corporate recruiter owning your pipeline end
                        to end
                      </span>
                    </li>}
                </ul>

                {!hasLeader && <div className="mt-auto pt-4 space-y-2.5" data-id="element-133" data-mp="pidjx0390y43">
                    <HiresRangeControl adSpend={adSpend} cpaLow={cpaLow} cpaHigh={cpaHigh} onAdSpendChange={onAdSpendChange} data-id="element-134" data-mp="ki97bl5gonj5" />
                    {recruiterButton}
                  </div>}
              </div>
        }}
        </FlipCard>

        {/* Performance */}
        <FlipCard flipped={openCards.performance} onToggle={() => toggleCard('performance')} spin={spin} label="Performance-Based Pricing" data-id="element-135" data-mp="1390lr14b344e">
          {{
          front: <div className="h-full bg-slate-900/40 border border-slate-800 rounded-xl p-6 flex flex-col" data-id="element-136" data-mp="ufdthkzb4izy">
                <div className="w-11 h-11 rounded-full bg-slate-800 flex items-center justify-center mb-4 border border-slate-700" data-id="element-137" data-mp="1qz4pd8hhv3ve">
                  <TrendingUp className="w-5 h-5 text-[#7FE0C3]" data-id="element-138" data-mp="1m5i9z8jy4lvw" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1" data-id="element-139" data-mp="3gg8k2nypuaum">
                  Performance-Based Pricing
                </h3>
                <p className="text-slate-400 text-xs mb-5" data-id="element-140" data-mp="36pnh6oc0ek5w">
                  {hasLeader ? 'Earned from day 30 while the leader stays' : 'Earned at retention milestones'}
                </p>
                <ul className="space-y-2.5" data-id="element-141" data-mp="2xnxnfbunn94y">
                  {(hasLeader ? leaderPerformanceInclusions : performanceInclusions).map(point => <li key={point} className="flex items-start" data-id="element-142" data-mp="2ns30i4jeerwd">
                      <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-143" data-mp="2spmgp9znpeqy" />
                      <span className="text-slate-300 text-base leading-snug" data-id="element-144" data-mp="lzeqncc9167m">
                        {point}
                      </span>
                    </li>)}
                </ul>
                <p className="mt-auto pt-4 text-slate-400 text-base leading-snug" data-id="element-145" data-mp="2bgad5d8alw1z">
                  {hasLeader ? 'You pay for the operator who stays and produces, not for the placement.' : 'You pay for the reps you retain, instead of resumés or hires.'}
                </p>
              </div>,
          back: <div className="h-full bg-slate-900/60 border border-[#7FE0C3]/30 rounded-xl p-6 flex flex-col" data-id="element-146" data-mp="2gdjyohsk9nkj">
                <div className="text-xs text-[#7FE0C3] uppercase tracking-widest mb-4" data-id="element-147" data-mp="2u8w5vonwl6ic">
                  Performance-Based Pricing
                </div>
                {hasLeader ? <div data-id="element-148" data-mp="3434u405widap">
                    <div className="text-white text-sm font-medium mb-1" data-id="element-149" data-mp="2z6e4al4ao3c3">
                      Leader retention bonus
                    </div>
                    <div className="flex items-baseline" data-id="element-150" data-mp="19rzkmf84v6ng">
                      <span className="text-4xl font-bold text-white" data-id="element-151" data-mp="14sd7e5sb1qen">
                        {usd(leaderActiveFee)}
                      </span>
                      <span className="text-slate-400 ml-2 text-sm" data-id="element-152" data-mp="iyejd363fp1a">
                        / month
                      </span>
                    </div>
                    <div className="text-slate-500 text-sm mt-1" data-id="element-153" data-mp="so7jn80schq8">
                      Starting day 30 active, capped at 6 payments
                    </div>
                  </div> : <div className="space-y-4" data-id="element-154" data-mp="lg1zrk5d3d">
                    <div data-id="element-155" data-mp="1wl02wqoqw2">
                      <div className="text-white text-sm font-medium mb-1" data-id="element-156" data-mp="4z6i84qde6ug">
                        Placement bonus
                      </div>
                      <div className="flex items-baseline" data-id="element-157" data-mp="9x7ykhwevs28">
                        <span className="text-4xl font-bold text-white" data-id="element-158" data-mp="excg8b922su1">
                          {usd(500)}
                        </span>
                        <span className="text-slate-400 ml-2 text-sm" data-id="element-159" data-mp="1lcmwpp0cwpyx">
                          per hire
                        </span>
                      </div>
                      <div className="text-slate-500 text-sm mt-1" data-id="element-160" data-mp="3auac2sym4zfv">
                        Day 15 active in the field
                      </div>
                    </div>
                    <div className="pt-4 border-t border-slate-800/50" data-id="element-161" data-mp="35xjv83ezhzte">
                      <div className="text-white text-sm font-medium mb-1" data-id="element-162" data-mp="3135xvvgrhim3">
                        Retention bonuses
                      </div>
                      <div className="flex items-baseline" data-id="element-163" data-mp="2r6kpd05j9pdi">
                        <span className="text-4xl font-bold text-white" data-id="element-164" data-mp="2r7vwn9y6tf9d">
                          {usd(1000)}
                        </span>
                        <span className="text-slate-400 ml-2 text-sm" data-id="element-165" data-mp="2i5mkygr6nfue">
                          per hire
                        </span>
                      </div>
                      <div className="text-slate-500 text-sm mt-1" data-id="element-166" data-mp="2d8misgxgex8k">
                        Day 45 and day 90 active in the field
                      </div>
                    </div>
                  </div>}
                <ul className="mt-5 space-y-2 pt-4 border-t border-slate-800/50" data-id="element-167" data-mp="139htc4jb4ays">
                  {(hasLeader ? leaderPerformanceInclusions : performanceInclusions).map(point => <li key={point} className="flex items-start" data-id="element-168" data-mp="tidfx99a4y62">
                      <Check className="w-5 h-5 text-[#7FE0C3] mr-2.5 mt-0.5 shrink-0" data-id="element-169" data-mp="okv7djz6ckyl" />
                      <span className="text-slate-400 text-base leading-snug" data-id="element-170" data-mp="kj01e26atjeh">
                        {point}
                      </span>
                    </li>)}
                </ul>
                <p className="mt-auto pt-5 text-slate-400 text-base leading-snug" data-id="element-171" data-mp="akkbu3mx6i73">
                  {hasLeader ? 'Starts at day 30 active and is billed only for the months the leader stays, capped at 6 payments.' : 'Billed only when a hire is still active in the field. You pay for the reps you retain, instead of resumés or hires.'}
                </p>
              </div>
        }}
        </FlipCard>
      </div>
    </div>;
}